import React from 'react';
import ReactPixel from 'react-facebook-pixel';
import moment from 'moment';
import queryString from 'query-string';
import browserStorage from './browserStorage';
import cookieConsentService from './cookieConsentService';

export const AnalyticsPlatforms = {
	FacebookPixel: 'FB',
	GoogleAnalytics: 'GA'
};

let enableFacebookPixel = false;
let enableGATracking = false;
let isUATrackingId = false;
let storedPixelId = null;

const gtag = (...args) => {
	if (typeof window !== 'undefined') {
		if (typeof window.gtag === 'undefined') {
			window.dataLayer = window.dataLayer || [];
			window.gtag = function gtag() {
				window.dataLayer.push(arguments);
			};
		}

		window.gtag(...args);
	}
};

// Google Consent Mode v2: set all ad/analytics signals to denied by default before any tag loads.
// security_storage and functionality_storage (essential) are always granted.
const setConsentDefault = () => {
	gtag('consent', 'default', {
		'ad_storage': 'denied',
		'analytics_storage': 'denied',
		'ad_user_data': 'denied',
		'ad_personalization': 'denied',
		'functionality_storage': 'granted',
		'security_storage': 'granted',
		'wait_for_update': 500
	});
};

// Google Consent Mode v2: update the signals to match the user's saved cookie preferences.
// analytics category => analytics_storage; marketing category => ad_storage/ad_user_data/ad_personalization.
const updateConsentMode = (preferences) => {
	const analyticsGranted = preferences && preferences.analytics ? 'granted' : 'denied';
	const marketingGranted = preferences && preferences.marketing ? 'granted' : 'denied';

	gtag('consent', 'update', {
		'analytics_storage': analyticsGranted,
		'ad_storage': marketingGranted,
		'ad_user_data': marketingGranted,
		'ad_personalization': marketingGranted
	});
};

const loadGTag = (googleAnalyticsCode) => {
	const gtagJs = document.createElement('script');
	gtagJs.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsCode}`;
	gtagJs.async = true;
	gtagJs.onload = () => {
		gtag('js', new Date());
		gtag('config', googleAnalyticsCode, {
			'linker': {
				'accept_incoming': true
			}
		});
	};
	document.body && document.body.appendChild(gtagJs);
};

const initAnalyticsTracking = (franchisor) => {
	const cookieConsent = cookieConsentService.getCookiePreferences(franchisor);
	const facebookPixelId = franchisor.trackFacebookPixel ? franchisor.facebookPixelId : '';
	const googleAnalyticsCode = franchisor.enableGoogleAnalytics ? franchisor.googleAnalyticsCode : '';

	if (facebookPixelId && cookieConsent.marketing) {
		const options = {
			autoConfig: false,
			debug: false,
		};
		ReactPixel.init(facebookPixelId, {}, options);
		storedPixelId = facebookPixelId;
		enableFacebookPixel = true;
	} else {
		enableFacebookPixel = false;
	}

	if (googleAnalyticsCode && cookieConsent.analytics) {
		// Queue Consent Mode v2 default (all denied) + update (per saved preferences) in the
		// dataLayer before loadGTag's onload fires gtag('config', ...).
		setConsentDefault();
		updateConsentMode(cookieConsent);

		loadGTag(googleAnalyticsCode);
		enableGATracking = true;
		isUATrackingId = googleAnalyticsCode.startsWith('UA-');	// Universal Analytics tracking id

		initCampaignTracking(getCampaignTags(franchisor));
	}
	else {
		enableGATracking = false;
	}
};

const getCampaignTags = (franchisor) => {
	let campaignTags = null;
	const storedCampaign = browserStorage.getFromLocalStorage(franchisor, 'campaign');
	if (storedCampaign) {
		// use from local storage within 7 days of saving
		const campaign = JSON.parse(storedCampaign);
		if (moment(campaign.timestamp).isAfter(moment().subtract(7, 'days'))) {
			campaignTags = campaign.tags;
		}
	}

	return campaignTags;
};

const setCampaignTags = (franchisor, campaignTags) => {
	if (campaignTags && cookieConsentService.getCookiePreferences(franchisor).marketing) {
		// store in local storage with current timestamp
		browserStorage.setToLocalStorage(franchisor, 'campaign', JSON.stringify({ tags: campaignTags, timestamp: moment() }));

		initCampaignTracking(campaignTags);
	}
};

const extractCampaignTagsFromQuery = query => {
	const queryParams = queryString.parse(query);
	let campaignTags = null;
	const campaignParams = Object.keys(queryParams).filter(x => x.startsWith('utm_'));
	if (campaignParams.length > 0) {
		campaignTags = {};
		campaignParams.forEach(key => {
			campaignTags[key] = queryParams[key];
		});
	}
	return campaignTags;
};

const storeCampaignTagsFromQuery = (franchisor, search) => {
	setCampaignTags(franchisor, extractCampaignTagsFromQuery(search));
};

const initCampaignTracking = (campaignTags) => {
	if (campaignTags && enableGATracking) {
		// doesn't work with UA
		//gtag('set', {
		//	'campaign_name': campaignTags.utm_campaign,
		//	'campaign_source': campaignTags.utm_source,
		//	'campaign_medium': campaignTags.utm_medium
		//});

		gtag('set', {
			campaign: {
				name: campaignTags.utm_campaign,
				source: campaignTags.utm_source,
				medium: campaignTags.utm_medium
			}
		});
	}
};

const updateAdvancedMatching = (parent) => {
	if (enableFacebookPixel && storedPixelId && parent) {
		const advancedMatching = {};
		if (parent.email) advancedMatching.em = parent.email.trim().toLowerCase();
		if (parent.firstName) advancedMatching.fn = parent.firstName.trim().toLowerCase();
		if (parent.lastName) advancedMatching.ln = parent.lastName.trim().toLowerCase();
		if (parent.phoneNumber) advancedMatching.ph = parent.phoneNumber.replace(/[\s\-()]/g, '');

		if (Object.keys(advancedMatching).length > 0) {
			ReactPixel.fbq('init', storedPixelId, advancedMatching);
		}
	}
};

const trackEvent = (eventCode, eventData, eventPlatform, isCustomEvent = false, eventId = null) => {
	const platforms = eventPlatform ? (Array.isArray(eventPlatform) ? eventPlatform : [eventPlatform]) : Object.values(AnalyticsPlatforms);

	if (enableFacebookPixel && platforms.includes(AnalyticsPlatforms.FacebookPixel)) {
		if (eventId) {
			// use raw fbq to pass eventID for deduplication
			ReactPixel.fbq(isCustomEvent ? 'trackCustom' : 'track', eventCode, eventData, { eventID: eventId });
		} else if (isCustomEvent) {
			ReactPixel.trackCustom(eventCode, eventData);
		} else {
			ReactPixel.track(eventCode, eventData);	// FB Pixel js has validation for standard events parameters
		}
	}

	if (enableGATracking && platforms.includes(AnalyticsPlatforms.GoogleAnalytics)) {
		gtag('event', eventCode, eventData);
	}
};

const trackPageView = (page) => {
	if (enableFacebookPixel) {
		ReactPixel.pageView();
	}

	// Manually send page views for Universal Analytics tracking IDs only.
	// GA4 Measurement IDs send page views automatically as long as "Enhanced measurement" => "Page views" => "Page changes based on browser history events" is enabled
	if (enableGATracking && isUATrackingId) {
		gtag('event', 'page_view', { page_path: page });
	}
};

export default {
	initAnalyticsTracking,
	updateConsentMode,
	updateAdvancedMatching,
	getCampaignTags,
	setCampaignTags,
	trackEvent,
	trackPageView
};

export const withAnalytics = Component =>
	class WithAnalytics extends React.Component {
		componentDidMount() {
			// capture UTM tags on any landing page before the page view is tracked
			storeCampaignTagsFromQuery(this.props.franchisor, this.props.location.search);

			const page = this.props.location.pathname;
			this.trackPage(page);
		}

		componentWillReceiveProps(nextProps) {
			if (this.props.location.search !== nextProps.location.search) {
				storeCampaignTagsFromQuery(nextProps.franchisor, nextProps.location.search);
			}

			const currentPage = this.props.location.pathname;
			const nextPage = nextProps.location.pathname;

			// trigger analytics tracking after franchisor details (FacebookPixelId, etc.) are loaded
			const currentFranchisor = this.props.franchisor;
			const nextFranchisor = nextProps.franchisor;

			if (currentPage !== nextPage || currentFranchisor.id !== nextFranchisor.id) {
				this.trackPage(nextPage);
			}
		}

		trackPage(page) {
			trackPageView(page);
		}

		render() {
			return <Component {...this.props} />;
		}
	};